import type {
  MarketBoardHtsApiItem,
  MarketBoardHtsApiResponse,
  MarketBoardQuoteApiRow,
  MarketBoardQuotesApiResponse,
} from '@app-types/summaryApi';

interface SelectedRicRefreshSnapshots {
  quotes: MarketBoardQuotesApiResponse;
  hts: MarketBoardHtsApiResponse;
}

const trimmedRic = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const findUnique = <T extends { ric?: string | null }>(
  rows: readonly T[],
  ric: string,
  source: string,
): T => {
  const matches = rows.filter((row) => trimmedRic(row?.ric) === ric);
  if (matches.length !== 1) {
    throw new Error(`${source} must contain exactly one selected RIC.`);
  }
  return matches[0];
};

const assertSuccessfulQuotes = (
  response: MarketBoardQuotesApiResponse | undefined,
  source: string,
): MarketBoardQuoteApiRow[] => {
  if (response?.status !== 0 || !Array.isArray(response.data)) {
    throw new Error(`${source} quotes snapshot is invalid.`);
  }
  return response.data;
};

const assertSuccessfulHts = (
  response: MarketBoardHtsApiResponse | undefined,
  source: string,
): MarketBoardHtsApiItem[] => {
  if (response?.status !== 0 || !Array.isArray(response.data)) {
    throw new Error(`${source} HTS snapshot is invalid.`);
  }
  return response.data;
};

export const buildSelectedRicRefreshSnapshots = ({
  ric: rawRic,
  currentQuotes,
  refreshedQuotes,
  currentHts,
  refreshedHts,
}: {
  ric: string;
  currentQuotes: MarketBoardQuotesApiResponse | undefined;
  refreshedQuotes: MarketBoardQuotesApiResponse;
  currentHts: MarketBoardHtsApiResponse | undefined;
  refreshedHts: MarketBoardHtsApiResponse;
}): SelectedRicRefreshSnapshots => {
  const ric = trimmedRic(rawRic);
  if (!ric) throw new Error('Selected RIC is invalid.');

  const currentQuoteRows = assertSuccessfulQuotes(currentQuotes, 'Current');
  const refreshedQuoteRows = assertSuccessfulQuotes(refreshedQuotes, 'Refreshed');
  const currentQuote = findUnique(currentQuoteRows, ric, 'Current quotes');
  const refreshedQuote = findUnique(refreshedQuoteRows, ric, 'Refreshed quotes');

  const nextQuote: MarketBoardQuoteApiRow = {
    ...currentQuote,
    closePrice: refreshedQuote.closePrice,
    netChange: refreshedQuote.netChange,
    high: refreshedQuote.high,
    low: refreshedQuote.low,
    dateTime: refreshedQuote.dateTime,
    dp: refreshedQuote.dp,
  };

  const currentHtsItems = assertSuccessfulHts(currentHts, 'Current');
  const refreshedHtsItems = assertSuccessfulHts(refreshedHts, 'Refreshed');
  const refreshedHtsItem = findUnique(refreshedHtsItems, ric, 'Refreshed HTS');
  if (refreshedHtsItem.response?.status !== 0) {
    throw new Error('Selected HTS item failed.');
  }

  const currentHtsMatches = currentHtsItems.filter((item) => trimmedRic(item?.ric) === ric);
  if (currentHtsMatches.length > 1) {
    throw new Error('Current HTS contains duplicate selected RICs.');
  }
  const nextHtsItem = currentHtsMatches.length === 1
    ? { ...currentHtsMatches[0], response: refreshedHtsItem.response }
    : { ric, response: refreshedHtsItem.response };
  const nextHtsItems = currentHtsMatches.length === 1
    ? currentHtsItems.map((item) => (item === currentHtsMatches[0] ? nextHtsItem : item))
    : [...currentHtsItems, nextHtsItem];

  return {
    quotes: {
      ...currentQuotes,
      data: currentQuoteRows.map((row) => (row === currentQuote ? nextQuote : row)),
    },
    hts: {
      ...currentHts,
      data: nextHtsItems,
    },
  };
};